import {
  ARRAY_CONFIGURATION_TYPE,
  AVAILABILITY_TYPES,
  PRIMITIVE_FIELDS_CONFIGURATION_TYPES,
} from './data-source-dynamic-form.constants';
import dataSourceDefinitionValidator from './generated-validators/data-source-definition-validator-generated';

// Schema for validation
export const formFieldValueSchema = {
  discriminator: 'type',
  mapping: {
    string: {
      properties: {
        value: { type: 'string' },
      },
    },
    number: {
      properties: {
        value: { type: 'uint32' },
      },
    },
    boolean: {
      properties: {
        value: { type: 'boolean' },
      },
    },
  },
} as const;

export const primitiveFieldDefinitionSchema = {
  properties: {
    label: { type: 'string' },
    name: { type: 'string' },
    description: { type: 'string' },
    type: { enum: PRIMITIVE_FIELDS_CONFIGURATION_TYPES },
    required: { type: 'boolean' },
  },
  optionalProperties: {
    validation: {
      elements: {
        discriminator: 'type',
        mapping: {
          pattern: {
            properties: {
              value: { type: 'string' },
              message: { type: 'string' },
            },
          },
          minimum: {
            properties: {
              value: { type: 'uint32' },
            },
            optionalProperties: {
              message: { type: 'string' },
            },
          },
          exclusiveMinimum: {
            properties: {
              value: { type: 'uint32' },
            },
            optionalProperties: {
              message: { type: 'string' },
            },
          },
          maximum: {
            properties: {
              value: { type: 'uint32' },
            },
            optionalProperties: {
              message: { type: 'string' },
            },
          },
          exclusiveMaximum: {
            properties: {
              value: { type: 'uint32' },
            },
            optionalProperties: {
              message: { type: 'string' },
            },
          },
          minLength: {
            properties: {
              value: { type: 'uint32' },
            },
            optionalProperties: {
              message: { type: 'string' },
            },
          },
          maxLength: {
            properties: {
              value: { type: 'uint32' },
            },
            optionalProperties: {
              message: { type: 'string' },
            },
          },
          oneOf: {
            properties: {
              value: { elements: { type: 'string' } },
            },
            optionalProperties: {
              message: { type: 'string' },
            },
          },
        },
      },
    },
    default: { type: 'string' },
    options: { elements: { type: 'string' } },
  },
} as const;

export const arrayFieldDefinitionSchema = {
  properties: {
    label: { type: 'string' },
    name: { type: 'string' },
    description: { type: 'string' },
    type: { enum: [ARRAY_CONFIGURATION_TYPE] },
    required: { type: 'boolean' },
    fields: { elements: primitiveFieldDefinitionSchema },
  },
  optionalProperties: {
    // Dummy for now, probably can remove this from this level, as it doesn't make sense to have it here
    validation: { elements: { type: 'string' } },
  },
} as const;

const fieldCommon = {
  properties: {
    label: { type: 'string' },
    name: { type: 'string' },
    description: { type: 'string' },
    required: { type: 'boolean' },
  },
  additionalProperties: true,
} as const;

const primitiveFieldPayload = {
  ...fieldCommon,
  optionalProperties: {
    validation: primitiveFieldDefinitionSchema.optionalProperties.validation,
    default: primitiveFieldDefinitionSchema.optionalProperties.default,
    options: primitiveFieldDefinitionSchema.optionalProperties.options,
  },
} as const;

// Need type here so it is included when reference in an array type
const primitiveFieldDefinitionUnionSchema = {
  discriminator: 'type',
  mapping: Object.fromEntries(PRIMITIVE_FIELDS_CONFIGURATION_TYPES.map((t) => [t, primitiveFieldPayload])),
} as const;

const arrayFieldPayload = {
  ...fieldCommon,
  properties: {
    ...fieldCommon.properties,
    fields: { elements: primitiveFieldDefinitionUnionSchema },
  },
  optionalProperties: {
    validation: arrayFieldDefinitionSchema.optionalProperties.validation,
  },
} as const;

export const fieldDefinitionSchema = {
  discriminator: 'type',
  // Build the mapping in one statement so TS keeps literal types
  mapping: {
    // Every primitive field type gets the same payload
    ...Object.fromEntries(PRIMITIVE_FIELDS_CONFIGURATION_TYPES.map((t) => [t, primitiveFieldPayload])),
    // The special “array” variant
    [ARRAY_CONFIGURATION_TYPE]: arrayFieldPayload,
  },
} as const;

export const fieldDefinitionSchemaStrong = {
  ...fieldDefinitionSchema,
  additionalProperties: false,
} as const;

export const sectionDefinitionSchema = {
  additionalProperties: true,
  properties: {
    name: { type: 'string' },
    advanced: { type: 'boolean' },
    fields: { elements: fieldDefinitionSchema },
  },
  optionalProperties: {
    id: { type: 'string' },
    label: { type: 'string' },
  },
} as const;

export const sectionDefinitionSchemaStrong = {
  ...sectionDefinitionSchema,
  properties: {
    ...sectionDefinitionSchema.properties,
    fields: { elements: fieldDefinitionSchemaStrong },
  },
  additionalProperties: false,
} as const;

export const dataSourceDefinitionSchema = {
  additionalProperties: true,
  properties: {
    groupLabel: { type: 'string' },
    type: { type: 'string' },
    availability: { enum: AVAILABILITY_TYPES },
    sections: { elements: sectionDefinitionSchema },
  },
  optionalProperties: {
    subGroupLabel: { type: 'string' },
    authGroup: { type: 'string' },
  },
} as const;

export const dataSourceDefinitionSchemaStrong = {
  ...dataSourceDefinitionSchema,
  additionalProperties: false,
} as const;

export const validateDataSourceDefinition = (dataSourceDefinition: unknown) => {
  const valid = dataSourceDefinitionValidator(dataSourceDefinition);
  return { valid, errors: dataSourceDefinitionValidator.errors };
};
